import {
  OnboardingAction,
  RefundMethod,
  RefundTiming,
  ReturnConditions as ReturnConditionsInfo
} from '@/domain/onboarding'
import {
  RefundPaymentMethodStrings,
  RefundTimingsStrings,
  ReturnMethodStrings
} from '@/utils'
import { Base, ReverCard } from '@itsrever/design-system'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MAX_RETURN_PERIOD_DAYS,
  isValidDayCount
} from '@/domain/return-conditions'
import { dayCountErrorCopy } from '@/application/manage-return-conditions'
import { CheckboxOption } from '../checkbox-option'

const { FieldRoot, FieldLabel, FieldDescription, FieldError, Input } = Base

interface ReturnConditionsProps {
  returnConditions: ReturnConditionsInfo
  dispatch: React.Dispatch<OnboardingAction>
  isShopify?: boolean
}

export const ReturnConditions: React.FC<ReturnConditionsProps> = ({
  returnConditions,
  dispatch,
  isShopify = false
}) => {
  const { t } = useTranslation()

  const updateRefundMethods = (method: RefundMethod) => {
    const refundMethods = returnConditions.refundMethods
    if (refundMethods.includes(method)) {
      refundMethods.splice(refundMethods.indexOf(method), 1)
    } else {
      refundMethods.push(method)
    }
    const fallbackBankTransfer =
      method === 'ORIGINAL' ? null : returnConditions.fallbackBankTransfer
    dispatch({
      type: 'UPDATE_RETURN_CONDITIONS',
      payload: {
        ...returnConditions,
        refundMethods,
        fallbackBankTransfer
      }
    })
  }
  const updateReturnMethods = (method: string) => {
    const returnMethods = returnConditions.returnMethods
    if (returnMethods.includes(method)) {
      returnMethods.splice(returnMethods.indexOf(method), 1)
    } else {
      returnMethods.push(method)
    }
    dispatch({
      type: 'UPDATE_RETURN_CONDITIONS',
      payload: { ...returnConditions, returnMethods }
    })
  }

  const handleChangeTiming = (timing: RefundTiming) => {
    dispatch({
      type: 'UPDATE_RETURN_CONDITIONS',
      payload: { ...returnConditions, timing }
    })
  }

  // Digits only, so a typed minus sign or decimal never reaches the state.
  const handleChangeReturnDays = (value: string) => {
    const digits = value.replace(/\D/g, '')
    dispatch({
      type: 'UPDATE_RETURN_CONDITIONS',
      payload: {
        ...returnConditions,
        returnDays: digits === '' ? null : parseInt(digits, 10)
      }
    })
  }

  const { returnDays } = returnConditions
  const invalidReturnDays =
    returnDays !== null && !isValidDayCount(returnDays, MAX_RETURN_PERIOD_DAYS)
  const returnDaysError = dayCountErrorCopy('returnPeriodDays', returnDays)

  const handleClickBankTransfer = (value: boolean) => {
    dispatch({
      type: 'UPDATE_RETURN_CONDITIONS',
      payload: { ...returnConditions, fallbackBankTransfer: value }
    })
  }

  return (
    <ReverCard className="p-4" fullWidth>
      <h6 className="mb-4">{t('onboarding.return_conditions')}</h6>
      <div className="mb-4 flex flex-col gap-4">
        <FieldRoot invalid={invalidReturnDays}>
          <FieldLabel>
            {t('onboarding.return_days')}
            <span className="text-error-main"> *</span>
          </FieldLabel>
          <FieldDescription>
            {t('onboarding.return_days_description')}
          </FieldDescription>
          <Input
            inputMode="numeric"
            placeholder="30"
            value={returnConditions.returnDays ?? ''}
            onChange={(e) => handleChangeReturnDays(e.currentTarget.value)}
          />
          {invalidReturnDays ? (
            <FieldError match>
              {t(returnDaysError.key, { max: returnDaysError.max })}
            </FieldError>
          ) : null}
        </FieldRoot>
        <FieldRoot>
          <FieldLabel>{t('onboarding.returns_policy')}</FieldLabel>
          <FieldDescription>
            {t('onboarding.returns_policy_description')}
          </FieldDescription>
          <Input
            value={returnConditions.returnsPolicy ?? ''}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_RETURN_CONDITIONS',
                payload: {
                  ...returnConditions,
                  returnsPolicy: e.currentTarget.value
                }
              })
            }}
          />
        </FieldRoot>
      </div>

      <div className="mb-4">
        <p className="font-semibold">
          {t('onboarding.refund_methods')}
          <span className="text-error-main"> *</span>
        </p>
        {Object.values(RefundPaymentMethodStrings).map((method, i) => {
          if (method === 'NO_PAYMENT_METHOD') return
          if (isShopify && method === 'PROMO_CODE') return
          if (!isShopify && method === 'GIFT_CARD') return
          return (
            <Fragment key={i}>
              <CheckboxOption
                label={t(`refund_methods.${method}`)}
                checked={returnConditions.refundMethods.includes(method)}
                onCheckedChange={() => updateRefundMethods(method)}
              />
              {method === 'ORIGINAL' &&
              returnConditions.refundMethods.includes(method) ? (
                <div className="mb-2 ml-4 mt-1 border-l border-solid border-grey-3 pl-2">
                  <p className="text-sm">
                    {t('onboarding.fallback_bank_transfer')}
                    <span className="text-error-main"> *</span>
                  </p>
                  <div className="flex items-center gap-4">
                    <CheckboxOption
                      size="sm"
                      label={t('onboarding.yes')}
                      checked={returnConditions.fallbackBankTransfer === true}
                      onCheckedChange={() => handleClickBankTransfer(true)}
                    />
                    <CheckboxOption
                      size="sm"
                      label={t('onboarding.no')}
                      checked={returnConditions.fallbackBankTransfer === false}
                      onCheckedChange={() => handleClickBankTransfer(false)}
                    />
                  </div>
                </div>
              ) : null}
            </Fragment>
          )
        })}
        <CheckboxOption
          label={t('refund_methods.exchange')}
          checked={returnConditions.refundMethods.includes('EXCHANGE')}
          onCheckedChange={() => updateRefundMethods('EXCHANGE')}
        />
      </div>
      <div>
        <p className="mb-2 font-semibold">
          {t('onboarding.timing')}
          <span className="text-error-main"> *</span>
        </p>
        {Object.values(RefundTimingsStrings).map((timing) => {
          if (timing === 'NO_TIMING') return
          return (
            <CheckboxOption
              key={timing}
              label={t(`refund_timings.${timing}`)}
              checked={returnConditions.timing === timing}
              onCheckedChange={() => handleChangeTiming(timing)}
            />
          )
        })}
      </div>

      <div className="mt-4">
        <p className="mb-2 font-semibold">
          {t('onboarding.return_methods')}
          <span className="text-error-main"> *</span>
        </p>
        {Object.values(ReturnMethodStrings).map((method) => {
          if (method === 'NO_RETURN_METHOD' || method === 'DELIVERY_PICKUP')
            return
          return (
            <CheckboxOption
              key={method}
              label={t(`return_methods.${method}`)}
              checked={returnConditions.returnMethods.includes(method)}
              onCheckedChange={() => updateReturnMethods(method)}
            />
          )
        })}
      </div>
    </ReverCard>
  )
}
